import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, MountainSnow, UtensilsCrossed, Waves, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import Stars from "../components/Stars";
import { FacilityIcon } from "../lib/facilityIcons";
import { AttractionsGrid } from "../components/AttractionsGrid";
import { cropStyle } from "../lib/cropStyle";
import { visibleRoomImages } from "../lib/roomImages";
import PromoBanner from "../components/PromoBanner";

function AvailabilitySearch() {
  const navigate = useNavigate();
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [guests, setGuests] = useState(2);
  const today = new Date().toISOString().slice(0, 10);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (ci) { params.set("check_in", ci); try { localStorage.setItem("ra_ci", ci); } catch { /* ignore */ } }
    if (co) { params.set("check_out", co); try { localStorage.setItem("ra_co", co); } catch { /* ignore */ } }
    params.set("guests", guests);
    try { localStorage.setItem("ra_guests", String(guests)); } catch { /* ignore */ }
    navigate(`/booking?${params.toString()}`);
  };

  return (
    <form
      data-testid="availability-search"
      onSubmit={submit}
      className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-5 grid grid-cols-1 sm:grid-cols-4 gap-4 border border-white/60"
    >
      <div>
        <label className="text-xs font-medium tracking-wide uppercase text-stone-500">Check in</label>
        <input data-testid="search-checkin" type="date" min={today} value={ci} onChange={(e) => setCi(e.target.value)} required
          className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
      </div>
      <div>
        <label className="text-xs font-medium tracking-wide uppercase text-stone-500">Check out</label>
        <input data-testid="search-checkout" type="date" min={ci || today} value={co} onChange={(e) => setCo(e.target.value)} required
          className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
      </div>
      <div>
        <label className="text-xs font-medium tracking-wide uppercase text-stone-500">Guests</label>
        <input data-testid="search-guests" type="number" min={1} max={12} value={guests} onChange={(e) => setGuests(e.target.value)}
          className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
      </div>
      <div className="flex items-end">
        <button data-testid="search-submit" type="submit" className="btn-primary w-full">Check Availability</button>
      </div>
    </form>
  );
}

export default function Home() {
  const { content } = useContent();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/reviews", { params: { featured: true } }).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const loaded = content !== null && content !== undefined;
  const heroes = content?.hero_images?.length ? content.hero_images : (loaded ? [
    "https://images.unsplash.com/photo-1618140052121-39fc6db33972?q=85&w=1600",
  ] : []);
  const captions = content?.hero_captions || [];
  const positions = content?.hero_positions || [];
  const [bucket, setBucket] = useState("desktop");
  useEffect(() => {
    const calc = () => { const w = window.innerWidth; setBucket(w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop"); };
    calc(); window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  const touchX = useRef(0);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const d = e.changedTouches[0].clientX - touchX.current;
    if (d < -50) setHeroIdx((i) => (i + 1) % heroes.length);
    else if (d > 50) setHeroIdx((i) => (i - 1 + heroes.length) % heroes.length);
  };
  useEffect(() => {
    if (heroes.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroes.length), 6000);
    return () => clearInterval(t);
  }, [heroes.length]);

  const ICONS = [MountainSnow, Waves, UtensilsCrossed, Sparkles];
  const hlSource = content?.home_highlights?.length ? content.home_highlights : [
    { title: "Mountain Sanctuary", text: "Panoramic Vumba views from every suite." },
    { title: "Osbourne Dam Trips", text: "Fishing, speedboating & lakeside braai." },
    { title: "Private Dining", text: "A personal chef & open-fire feasts." },
    { title: "Curated Experiences", text: "Leopard Rock evenings & guided hunts." },
  ];
  const highlights = hlSource.map((h, i) => ({ ...h, icon: ICONS[i % ICONS.length] }));
  const featuredFac = (() => {
    const lim = content?.facility_strip_limit ?? 6;
    const feat = (content?.facilities || []).filter((f) => f.featured);
    return lim > 0 ? feat.slice(0, lim) : feat;
  })();

  return (
    <div>
      <PromoBanner />
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] overflow-hidden grain" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {heroes.map((img, i) => (
          <div key={img} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === heroIdx ? 1 : 0 }}>
            <img src={img} alt="The Rose Air BnB" className="h-full w-full object-cover" style={{ objectPosition: positions[i]?.[bucket] || "center" }} />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/30 to-black/40" />
        <div className="relative h-full container-lux flex flex-col justify-center pt-24 sm:pt-0">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="eyebrow mb-4">
            Mutare · Zimbabwe · Est. Highlands Retreat
          </motion.span>
          <AnimatePresence mode="wait">
            {captions[heroIdx] && (
              <motion.div
                key={heroIdx}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.7 }}
                data-testid="hero-caption"
                className="text-gold font-light tracking-wide text-lg sm:text-xl mb-2"
              >
                {captions[heroIdx]}
              </motion.div>
            )}
          </AnimatePresence>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[1.05] max-w-3xl"
          >
            {content?.hero_title || "A Rose Among the Mountains"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 text-cream/85 text-lg max-w-xl font-light"
          >
            {content?.hero_subtitle || "An exclusive luxury retreat nestled in the misty highlands of Mutare, Zimbabwe."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-4">
            <button data-testid="hero-book-button" onClick={() => navigate("/booking")} className="btn-primary">Reserve Your Stay</button>
            <button onClick={() => navigate("/accommodation")} className="btn-outline">Explore Suites</button>
          </motion.div>
          {heroes.length > 1 && (
            <div className="mt-8 flex gap-2" data-testid="hero-dots">
              {heroes.map((src, i) => (
                <button key={src} onClick={() => setHeroIdx(i)} data-testid={`hero-dot-${i}`} aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === heroIdx ? "w-7 bg-gold" : "w-2.5 bg-cream/50 hover:bg-cream/80"}`} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Availability search — own section, no overlap on any screen */}
      <section className="container-lux py-8 relative z-10" data-testid="availability-section">
        <AvailabilitySearch />
      </section>

      {/* Welcome */}
      <section className="pt-8 sm:pt-12 pb-20 container-lux grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="eyebrow">Welcome</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest mt-3 leading-tight">
            {content?.welcome_title || "Welcome to your mountain sanctuary"}
          </h2>
          <p className="mt-6 text-stone-600 leading-relaxed text-base">
            {content?.welcome_text}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {highlights.map((h) => (
              <div key={h.title} className="flex gap-3">
                <div className="h-11 w-11 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                  <h.icon className="text-rose" size={20} />
                </div>
                <div>
                  <div className="font-semibold text-forest text-sm">{h.title}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{h.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {(() => {
            const imgs = content?.welcome_images?.filter((w) => !w.hidden && w.url).length
              ? content.welcome_images.filter((w) => !w.hidden && w.url)
              : [{ url: heroes[0] }, { url: visibleRoomImages(rooms[0])[0] || heroes[0] }];
            return imgs.map((w, i) => (
              <img key={i} src={w.url} alt={w.title || "Retreat"} className={`rounded-2xl h-72 w-full object-cover object-center ${imgs.length === 1 ? "col-span-2" : ""}`} />
            ));
          })()}
        </div>
      </section>

      {featuredFac.length > 0 && (
        <section className="py-12 border-y border-stone-100 bg-white" data-testid="home-facility-strip">
          <div className="container-lux text-center">
            <span className="eyebrow">Everything you need</span>
            <h2 className="font-serif text-2xl md:text-3xl text-forest mt-1 mb-6">Comforts &amp; amenities</h2>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {featuredFac.map((f, i) => (
                <button key={`${f.label}-${i}`} onClick={() => navigate(f.id ? `/facilities/${f.id}` : "/facilities")} className="flex items-center gap-2 text-sm font-medium text-forest hover:text-rose transition-colors" data-testid={`home-facility-${f.label}`}>
                  <FacilityIcon name={f.icon} size={18} className="text-rose" /> {f.label}
                </button>
              ))}
            </div>
            <button onClick={() => navigate("/facilities")} data-testid="home-facilities-cta" className="btn-dark mt-8 inline-flex">Explore all facilities</button>
          </div>
        </section>
      )}

      {/* Featured rooms */}
      <section className="py-20 bg-sand/60">
        <div className="container-lux">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="eyebrow">Accommodation</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2">Signature suites & chalets</h2>
            </div>
            <button onClick={() => navigate("/accommodation")} className="hidden sm:inline-flex items-center gap-1 text-rose font-semibold text-sm hover:gap-2 transition-all">
              View all <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {rooms.map((room, i) => (
              <motion.div
                key={room.id} data-testid={`home-room-${room.id}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card-lux overflow-hidden cursor-pointer" onClick={() => navigate("/accommodation")}
              >
                <div className="h-56 overflow-hidden">
                  <img src={visibleRoomImages(room)[0]} alt={room.name} className="h-full w-full object-cover object-center hover:scale-105 transition-transform duration-500" style={cropStyle(room.image_positions?.[visibleRoomImages(room)[0]], room.image_zooms?.[visibleRoomImages(room)[0]])} />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-forest">{room.name}</h3>
                  <p className="text-sm text-stone-500 mt-2 line-clamp-2">{room.short}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-rose font-semibold">${room.price} <span className="text-stone-400 font-normal text-sm">/ night</span></span>
                    <span className="text-xs text-stone-400">{room.beds} · {room.size}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 container-lux">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="eyebrow">Guest Stories</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2">Loved by travellers</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.slice(0, 3).map((r) => (
            <div key={r.id} className="card-lux p-7">
              <Stars value={r.rating} />
              <h3 className="font-serif text-xl text-forest mt-4">{r.title}</h3>
              <p className="text-sm text-stone-600 mt-3 leading-relaxed line-clamp-4">"{r.text}"</p>
              <div className="mt-5 flex items-center gap-2 text-sm">
                <span className="font-semibold text-forest">{r.author}</span>
                {r.verified && <span className="text-[11px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full">✓ Verified via {r.source}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Manage booking */}
      <section className="py-14 bg-sand">
        <div className="container-lux flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left">
          <div>
            <span className="eyebrow">Already booked?</span>
            <h2 className="font-serif text-2xl md:text-3xl text-forest mt-1">Manage your reservation</h2>
            <p className="text-sm text-stone-500 mt-1">View, modify or cancel your stay using your booking code and email.</p>
          </div>
          <button onClick={() => navigate("/manage")} data-testid="home-manage-booking" className="btn-primary shrink-0">Manage My Booking</button>
        </div>
      </section>

      {/* Attractions */}
      <section className="py-20 bg-forest text-cream">
        <div className="container-lux">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Nearby</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-2">Local attractions & escapes</h2>
          </div>
          <AttractionsGrid attractions={content?.attractions || []} />
          <div className="text-center mt-12">
            <button onClick={() => navigate("/experiences")} className="btn-primary">Discover Experiences</button>
          </div>
        </div>
      </section>
    </div>
  );
}
